// src/api/feedbackApi.ts

import { FeedbackRequest, FeedbackResponse } from "../interfaces/feedbackTypes";
import { createLogger } from "../utils/logger";
import { constructRequest } from "./utils/apiHandler";
import { handleAuthRejection } from "./utils/errorHandler";
import { createClientErrorInfo } from "./utils/clientErrorHandler";
import { streamErrorToPostHog, ApiNames } from "./utils/captureError";
const log = createLogger("FeedbackApi");

// Use environment variable for API base URL with fallback
const API_BASE_URL = process.env.API_BASE_URL;

/**
 * Custom error class for feedback API errors
 */
export class FeedbackApiError extends Error {
  errorCode: string;
  statusCode: number;
  requestId?: string;

  constructor(message: string, errorCode: string, statusCode: number, requestId?: string) {
    super(message);
    this.name = "FeedbackApiError";
    this.errorCode = errorCode;
    this.statusCode = statusCode;
    this.requestId = requestId;
  }
}

/**
 * Submit user feedback for an AI response
 * @param request Feedback request payload
 * @returns Promise with feedback response
 */
export async function submitFeedback(request: FeedbackRequest): Promise<FeedbackResponse> {
  const url = `${API_BASE_URL}/feedback/submit`;

  log.info("Submitting feedback to API", {
    url,
    requestId: request.request_id,
    rating: request.rating,
    hasComment: !!request.comment,
    accessToken: request.accessToken,
  });

  try {
    // Create FormData with request payload
    const formData = new FormData();
    formData.append("request_id", request.request_id);
    formData.append("rating", request.rating);

    if (request.comment) {
      formData.append("comment", request.comment);
    }
    if (request.session_id) {
      formData.append("session_id", request.session_id);
    }
    if (request.doc_id) {
      formData.append("doc_id", request.doc_id);
    }
    if (request.agent_id) {
      formData.append("agent_id", request.agent_id);
    }
    if (request.metadata) {
      formData.append("metadata", JSON.stringify(request.metadata));
    }

    // Use constructRequest utility to handle authorization
    const apiRequest = constructRequest(url, "POST", request.accessToken || null, formData);

    const response = await fetch(apiRequest);

    // Emit the auth-rejection telemetry and, on 401, trigger the token refresh
    // bridge. Called BEFORE the body is consumed so a non-JSON 401 body (e.g.
    // a WAF block page) still triggers the refresh.
    handleAuthRejection(response);

    // Parse response
    const data = await response.json();

    if (!response.ok) {
      log.error("Feedback API returned error", {
        status: response.status,
        data,
      });

      throw new FeedbackApiError(
        data.message || "Failed to submit feedback",
        data.error_code || "FEEDBACK_ERROR",
        response.status,
        data.request_id
      );
    }

    log.info("Feedback submitted successfully", {
      feedbackId: data.feedback_id,
      status: data.status,
    });

    return data as FeedbackResponse;
  } catch (error) {
    // If it's already a FeedbackApiError, rethrow it
    if (error instanceof FeedbackApiError) {
      streamErrorToPostHog(error, ApiNames.FEEDBACK_API);
      throw error;
    }

    // Handle client-side errors with helpful, contextual messages
    log.error("Failed to submit feedback", {
      error: error instanceof Error ? error.message : String(error),
    });

    const clientError = createClientErrorInfo(error, "general");
    const clientApiError = new FeedbackApiError(clientError.message, clientError.errorCode, 0);
    streamErrorToPostHog(clientApiError, ApiNames.FEEDBACK_API);
    throw clientApiError;
  }
}
